<template>

    <div id="invite">

        <div class="mb-4">
            <h1 class="text-2xl font-medium text-gray-900 capitalize">Account Invite</h1>
        </div>

        <div class="flex flex-col" v-if="loaded">

            <ul class="top-help-block">
                <li><span class="text-brand-600">*</span> denotes required field</li>
            </ul>

            <div class="panel">
                <div class="panel-form">
                    <div class="panel-title">
                        <h3>Account Information</h3>
                        <p>
                            Your company/organisation settings.
                        </p>
                    </div>
                    <div class="panel-section">
                        <div>
                            <div class="grid grid-cols-6 gap-6">
                                <div class="form-control col-span-6 sm:col-span-4 mt-4">
                                    <h2 class="text-lg font-medium text-gray-900">{{ current_user.account }}</h2>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            <form action="#" method="POST" @submit.prevent="inviteAccountTeamMember(current_user.account_id)">
                <div class="panel">
                    <div class="panel-form">
                        <div class="panel-title">
                            <h3>User Information</h3>
                            <p>
                                The user information of the team member.
                            </p>
                        </div>
                        <div class="panel-section">
                            <div class="grid grid-cols-6 gap-6">
                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('name') }">
                                    <label for="first_name" class="block text-sm font-medium text-gray-700">Name<i></i></label>
                                    <input type="text" name="first_name" id="first_name" v-model="form.data.name">
                                    <span v-if="form.fieldHasErrors('name')">{{ form.getFieldError('name') }}</span>
                                </div>

                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('email') }">
                                    <label for="email_address" class="block text-sm font-medium text-gray-700">Email<i></i></label>
                                    <input type="text" name="email_address" id="email_address" v-model="form.data.email">
                                    <span v-if="form.fieldHasErrors('email')">{{ form.getFieldError('email') }}</span>
                                </div>

                                <div class="form-control col-span-6 xl:col-span-4" :class="{ 'form-control-error': form.fieldHasErrors('role') }" v-if="$parent.isAdmin">
                                    <label for="role" class="block text-sm font-medium text-gray-700">Role</label>
                                    <select name="role" id="role" v-model="form.data.role">
                                        <option :value="null">No Role</option>
                                        <option :value="role" v-for="role in roles">{{ role }}</option>
                                    </select>
                                    <span v-if="form.fieldHasErrors('role')">{{ form.getFieldError('role') }}</span>
                                </div>

                                <p class="flex col-span-6 -mt-4 text-sm text-gray-500 pb-4">
                                    &nbsp;
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="flex mt-6 justify-end">
                    <div class="flex border-l-4 border-brand-500 bg-white text-gray-700 px-4 py-1 rounded tracking-wide shadow items-center text-brand-400" v-if="form.successful()">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <p class="text-gray-700 text-sm">
                            {{ form.getSuccessMessage() }}
                        </p>
                    </div>

                    <div class="flex border-l-4 border-error-500 bg-error-50 text-gray-700 px-4 py-1 rounded tracking-wide shadow items-center text-error-400" v-if="form.hasError()">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <p class="text-error-500 text-sm">
                            {{ form.getErrorMessage() }}
                        </p>
                    </div>
                    <button type="submit" class="btn-outline justify-center px-6 ml-6" @click.prevent="goBack">
                        Go Back
                    </button>
                    <button type="submit" class="btn justify-center px-6 ml-4" :disabled="form.isFormUpdating()">
                        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" v-if="form.isFormUpdating()">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Invite Team Member
                    </button>
                </div>
            </form>

            <div class="mb-4">
                <h1 class="text-2xl font-medium text-gray-900 capitalize">Account Team Members</h1>
            </div>

            <div class="index-table pt-4" v-if="form.getAllAccountUsers()">
                <div>
                    <div>
                        <table>
                            <thead>
                            <tr>
                                <th scope="col" class="hidden lg:table-cell text-left tracking-wider">
                                    &nbsp;
                                </th>
                                <th scope="col" class="text-left tracking-wider">
                                    Name
                                </th>
                                <th scope="col" class="hidden lg:table-cell text-left tracking-wider">
                                    Status
                                </th>
                                <th scope="col" class="hidden lg:table-cell text-left tracking-wider">
                                    &nbsp;
                                </th>
                                <th scope="col" class="hidden lg:table-cell text-left tracking-wider" v-if="$parent.isAdmin">
                                    Role
                                </th>
                                <th scope="col" class="text-center tracking-wider">
                                    Action
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                                <tr class="colored-row" v-for="user in account_users">

                                    <td class="hidden lg:table-cell">&nbsp;</td>
                                    <td>
                                        <span class="font-medium">{{ user.name }}</span>
                                        <br>
                                        <span class="info">{{ user.email }}</span>
                                        <br>
                                        <span class="font-medium text-gray-400">{{ user.account }}</span>
                                    </td>
                                    <td class="hidden lg:table-cell">
                                        <span class="status-box status-complete" v-if="user.active">
                                            Active
                                        </span>
                                        <span class="status-box status-cancelled" v-else>
                                            Inactive
                                        </span>
                                        <br>
                                    </td>
                                    <td class="hidden lg:table-cell text-right text-xs">
                                        <span class="text-sm">Invited {{ formatDate(user.created_at) }}</span><br>
                                        <span class="text-xs text-gray-400">Last updated {{ formatDate(user.updated_at) }}</span>
                                    </td>
                                    <td v-if="$parent.isAdmin">
                                        <div class="form-control col-span-6 xl:col-span-4">
                                            <select name="role" id="user-role" v-model="user.role" @change="updateRole(user)" :disabled="$parent.auth.id == user.id">
                                                <option :value="null">No Role</option>
                                                <option :value="role" v-for="role in roles">{{ role }}</option>
                                            </select>
                                        </div>
                                    </td>
                                    <td class="text-right">
                                        <button class="btn-outline text-xs" @click="resendInvite(user.id)">
                                            Resend Invite
                                        </button>
                                        <button class="btn-error text-xs" @click="deleteAccount(user.id)">
                                           Delete Account
                                        </button>
                                    </td>
                                </tr>

                                <tr class="py-6 text-xs font-medium">
                                    <td colspan="6" class="text-center">
                                        <infinite-loading @infinite="infiniteHandler">
                                            <div slot="spinner">Loading...</div>
                                            <div slot="no-more">No more team members</div>
                                        </infinite-loading>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div class="panel" v-else>
                <div class="p-10">
                    <div class="text-gray-200 my-6">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-20 w-20 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                    </div>

                    <h2 class="text-gray-600 font-medium text-2xl text-center">No Team Members Available</h2>

                    <p class="text-center mt-4 text-sm text-gray-500">
                        We could not find any team members.
                    </p>
                </div>
            </div>
        </div>

        <Loading v-else></Loading>

    </div>

</template>

<script>
import {User} from "../../app/Auth/User";
import Loading from "../Support/Loading";
import Address from "../Support/Address";
import InfiniteLoading from "vue-infinite-loading";
import {FormattingMixin, NavigationMixin, PermissionMixin} from "../../support/mixins";

export default {
    name: "AccountInvite",
    mixins: [PermissionMixin, NavigationMixin, FormattingMixin],
    data() {
        return {
            form: null,
            current_user: null,
            current_account: null,
            loaded: false,
            account_users: null,
            roles: ['admin', 'prepaid']
        }
    },
    components: {
        Loading,
        Address,
        InfiniteLoading
    },
    methods: {
        inviteAccountTeamMember(id) {
            let vm = this;

            vm.form.accountInvite(id).then(result => {
                vm.form.getAllAccountUsers().then(response => {
                    vm.account_users = response;
                }).catch(error => vm.handleError(error));
            })
        },
        infiniteHandler($state) {
            let vm = this;

            vm.form.page += 1;

            if (vm.form.indexHasMoreResults()) {
                this.form.index(true).then(() => {
                    $state.loaded();
                });
            } else {
                $state.loaded();
                $state.complete();
            }
        },

        deleteAccount(index)
        {
            if (confirm('Are you sure you want to delete user account?')) {
                let vm = this;
                vm.form.deleteAccountUsers(index).then(() => {
                    vm.account_users = vm.account_users.filter((t) => {
                        return t.id !== index
                    })
                }).catch(error => vm.handleError(error));
            }
        },

        resendInvite(index)
        {
            if (confirm('Are you sure you want to resend invite to account?')) {
                let vm = this;
                vm.form.resendAccountUserInvite(index).catch(error => vm.handleError(error));
            }
        },

        updateRole(user)
        {
            let vm = this;
            vm.form.updateUserRole(user).catch(error => vm.handleError(error));
        },
    },
    mounted() {
        let vm = this;

        new Promise(resolve => {
            vm.form = new User();

            vm.form.current().then(response => {
                vm.current_user = response;
                if(response.amount_threshold > 0){
                    vm.form.data.amount_threshold = response.amount_threshold / 100
                }
                resolve(true);
            }).catch(error => vm.handleError(error));


            vm.form.getAllAccountUsers().then(response => {
                vm.account_users = response;
                resolve(true);
            }).catch(error => vm.handleError(error));

        }).then(result => {
            vm.loaded = result;
        });
    }
}
</script>


